import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { onlineManager } from "@tanstack/react-query";
import { useDeals } from "@/pages/deals/hooks";
import {
  useDeleteProposal,
  useIsProposalDeleted,
  useProposalDocument,
  useProposalTemplate,
  useRegenerateProposal,
  useRenameProposal,
  useReviseProposal,
  useWithdrawProposal,
  useSaveProposal,
  useSendProposal
} from "../hooks";
import {
  Proposal,
  ProposalEditorLocationState,
  ProposalVersion
} from "../types";
import { ProposalEditor } from "./ProposalEditor";
import { useGenerateBlockedReason } from "../termsReadiness";

// Each proposal opens afresh, with its own read of the stored version.
export const ProposalEditorPage: React.FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  return <OpenedProposal key={proposalId} />;
};

const OpenedProposal: React.FC = () => {
  const { dealId = "", proposalId } = useParams<{
    dealId: string;
    proposalId: string;
  }>();
  const [openedAt] = useState(() => Date.now());
  const location = useLocation();
  const navigate = useNavigate();
  // Counts the Revises that landed; the editor reopens on each new draft.
  const [revision, setRevision] = useState(0);
  // Read on each arrival, then cleared from history below, so a reload doesn't
  // rebuild the draft again. Withdraw and Regenerate from the sent proposal
  // arrives here again, after its Revise, so it rebuilds that new draft only.
  const [opened, setOpened] = useState(() => ({
    with: (location.state as ProposalEditorLocationState | null) ?? {},
    revision: 0
  }));
  const openedWith = opened.with;
  const regenerateOnOpen =
    !!openedWith.regenerate && opened.revision === revision;
  // Only an in-app path, never a URL handed in from elsewhere.
  const returnTo =
    openedWith.returnTo?.startsWith("/") &&
    !openedWith.returnTo.startsWith("//")
      ? openedWith.returnTo
      : undefined;
  useEffect(() => {
    const state = location.state as ProposalEditorLocationState | null;
    if (state?.regenerate || state?.recipients) {
      setOpened({ with: state, revision });
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: returnTo ? { returnTo } : null
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const { entities: deals } = useDeals(dealId ? [dealId] : [], {
    enabled: !!dealId
  });
  const deal = deals[0];
  const contractId = deal?.contractId ?? undefined;
  const {
    data: cached,
    dataUpdatedAt,
    isError,
    isFetching,
    isPaused,
    refetch: refetchDocument
  } = useProposalDocument(dealId, proposalId);
  // A stored proposal shows once read since it opened, never as cached before.
  const document =
    cached && (!cached.saved || dataUpdatedAt >= openedAt) ? cached : undefined;
  // Can't open: the read failed and none is running, or it can't run while offline
  const openError =
    (isError && !isFetching) ||
    (isPaused && !document && !onlineManager.isOnline());
  const deleted = useIsProposalDeleted(dealId, proposalId);
  const { data: template } = useProposalTemplate();
  const { reason: regenerateBlockedReason, isLoading: termsLoading } =
    useGenerateBlockedReason(dealId, contractId);
  const { mutateAsync: onSave } = useSaveProposal(dealId);
  const { mutateAsync: rename } = useRenameProposal(dealId);
  const { mutateAsync: regenerate } = useRegenerateProposal();
  const { mutateAsync: remove } = useDeleteProposal(dealId);
  const { mutateAsync: send } = useSendProposal(dealId);
  const { mutateAsync: revise } = useReviseProposal(dealId);
  const { mutateAsync: withdraw } = useWithdrawProposal(dealId);
  const onRename = useCallback(
    (proposal: Proposal, name: string) =>
      rename({ proposalId: proposal.id, name }),
    [rename]
  );
  const onRegenerate = useCallback(
    (proposal: Proposal, saved: boolean) =>
      regenerate({ proposal, dealId, saved }),
    [regenerate, dealId]
  );
  const onDelete = useCallback(
    (proposal: Proposal) => remove(proposal.id),
    [remove]
  );
  const onSend = useCallback(
    (proposal: Proposal, basedOn: ProposalVersion) =>
      send({ proposalId: proposal.id, basedOn }),
    [send]
  );
  // Based on the version on screen; once the new draft is read back, the
  // editor reopens on it.
  const onRevise = useCallback(
    async (proposal: Proposal) => {
      const stored = await revise({
        proposalId: proposal.id,
        basedOn: document?.version
      });
      setRevision((current) => current + 1);
      return stored.proposal;
    },
    [revise, document?.version]
  );
  // Based on the version on screen; the proposal is then read again, Voided.
  const onWithdraw = useCallback(
    (proposal: Proposal) =>
      withdraw({ proposalId: proposal.id, basedOn: document?.version }),
    [withdraw, document?.version]
  );

  return (
    <ProposalEditor
      dealId={dealId}
      returnTo={returnTo}
      dealName={deal?.name}
      sections={template?.sections ?? []}
      document={document}
      openError={openError}
      deleted={deleted}
      onRetryOpen={() => refetchDocument()}
      saveUnavailable={!deal}
      regenerateOnOpen={regenerateOnOpen}
      regenerateBlockedReason={regenerateBlockedReason}
      regenerateHeld={!deal || termsLoading}
      initialRecipients={openedWith.recipients}
      onSave={onSave}
      onRename={onRename}
      onRegenerate={onRegenerate}
      onDelete={onDelete}
      onSend={onSend}
      onRevise={onRevise}
      onWithdraw={onWithdraw}
      revision={revision}
    />
  );
};
